/**
 * The Account detail view's state selection (design D15/D17).
 *
 * The detail page has exactly one server-side signal about the account itself:
 * the **row count** of the single-row `account_id` query of the account list
 * endpoint. That query returns one row for any account that exists under the
 * selected tenant — including an account holding no bucket and no entry — and
 * zero rows when no such account exists under that tenant, whether because it
 * exists nowhere or because it belongs to someone else.
 *
 * So the row count is the whole successful state machine, carrying exactly one
 * bit: existence under the selected tenant. Emptiness is carried *inside* the
 * row, never by its absence. A failed request (including
 * `403 application_forbidden`) is neither successful state — it is an error.
 */

export type DetailState = 'loading' | 'error' | 'no-data' | 'account'

export interface DetailStateInput {
  loading: boolean
  /** The server's error message, when the detail query actually failed. */
  error: string | null
  /** True only after a successful single-row account query. */
  loaded: boolean
  /** 0 or 1 — the account query returns at most one row. */
  rowCount: number
}

export function resolveDetailState(input: DetailStateInput): DetailState {
  if (input.error) return 'error'
  if (input.loading || !input.loaded) return 'loading'
  return input.rowCount === 0 ? 'no-data' : 'account'
}

export type BalanceArea = 'no-buckets' | 'cards'

/**
 * Inside a rendered account, an empty `sub_accounts` array is an *inner* empty
 * state — never the no-data state and never an error, because the row proves
 * the account exists under this tenant.
 */
export function resolveBalanceArea(subAccountCount: number): BalanceArea {
  return subAccountCount === 0 ? 'no-buckets' : 'cards'
}

export type LedgerArea = 'loading' | 'error' | 'empty' | 'table'

export interface LedgerAreaInput {
  loading: boolean
  error: string | null
  loaded: boolean
  rowCount: number
}

/** The ledger table's own area: an error is an error, empty is only ever empty. */
export function resolveLedgerArea(input: LedgerAreaInput): LedgerArea {
  if (input.error) return 'error'
  if (input.loading || !input.loaded) return 'loading'
  return input.rowCount === 0 ? 'empty' : 'table'
}
